import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Filter, X } from "lucide-react";
import AnimatedSection from "../components/common/AnimatedSection";
import SectionHeading from "../components/common/SectionHeading";

const projects = [
  {
    id: 1,
    title: "Modern Minimalist 3BHK",
    location: "Kondapur, Hyderabad",
    type: "3BHK",
    category: "b2c",
    budget: "premium",
    rooms: ["kitchen", "wardrobe", "living"],
    images: [
      "https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=800&q=80",
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80",
      "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=800&q=80",
    ],
    description:
      "Complete home interiors with modular kitchen, wardrobes, and living room storage.",
  },
  {
    id: 2,
    title: "Luxury Villa Interiors",
    location: "Narsingi, Hyderabad",
    type: "villa",
    category: "b2c",
    budget: "luxury",
    rooms: ["kitchen", "wardrobe", "living", "dining", "study"],
    images: [
      "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=800&q=80",
      "https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=800&q=80",
    ],
    description:
      "Full villa interiors with custom designs and premium finishes throughout.",
  },
  {
    id: 3,
    title: "Compact 2BHK Rental",
    location: "Gachibowli, Hyderabad",
    type: "2bhk",
    category: "b2c",
    budget: "standard",
    rooms: ["kitchen", "wardrobe"],
    images: [
      "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800&q=80",
    ],
    description:
      "Smart rental package with modular kitchen and wardrobes, optimized for budget.",
  },
  {
    id: 4,
    title: "Premium Island Kitchen",
    location: "Madhapur, Hyderabad",
    type: "kitchen",
    category: "b2c",
    budget: "premium",
    rooms: ["kitchen"],
    images: [
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80",
      "https://images.unsplash.com/photo-1600585152220-90363fe7e115?w=800&q=80",
    ],
    description:
      "Stunning island kitchen with BWP Ply construction and premium hardware.",
  },
  {
    id: 5,
    title: "Builder Model House",
    location: "Kukatpally, Hyderabad",
    type: "model-house",
    category: "b2b",
    budget: "premium",
    rooms: ["kitchen", "wardrobe", "living", "bedroom"],
    images: [
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80",
      "https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=800&q=80",
    ],
    description:
      "Complete model house interiors for a leading builder, showcasing quality and design.",
  },
  {
    id: 6,
    title: "Spacious 4BHK",
    location: "Banjara Hills, Hyderabad",
    type: "4bhk",
    category: "b2c",
    budget: "luxury",
    rooms: ["kitchen", "wardrobe", "living", "dining", "study", "pooja"],
    images: [
      "https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=800&q=80",
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=800&q=80",
    ],
    description:
      "Luxury 4BHK with full home interiors including pooja room and custom study.",
  },
  {
    id: 7,
    title: "Walk-in Wardrobe",
    location: "Jubilee Hills, Hyderabad",
    type: "wardrobe",
    category: "b2c",
    budget: "luxury",
    rooms: ["wardrobe"],
    images: [
      "https://images.unsplash.com/photo-1558997519-83ea9252edf8?w=800&q=80",
    ],
    description:
      "Custom walk-in wardrobe with HDHMR construction and designer fittings.",
  },
  {
    id: 8,
    title: "Bulk Kitchen Project",
    location: "Miyapur, Hyderabad",
    type: "bulk-kitchen",
    category: "b2b",
    budget: "standard",
    rooms: ["kitchen"],
    images: [
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80",
    ],
    description:
      "24 identical kitchens for apartment complex, delivered with factory consistency.",
  },
];

const filters = {
  category: [
    { id: "all", label: "All Projects" },
    { id: "b2c", label: "Homeowners" },
    { id: "b2b", label: "Builders/Architects" },
  ],
  rooms: [
    { id: "kitchen", label: "Kitchen" },
    { id: "wardrobe", label: "Wardrobe" },
    { id: "living", label: "Living" },
    { id: "bedroom", label: "Bedroom" },
    { id: "dining", label: "Dining" },
    { id: "study", label: "Study" },
  ],
  budget: [
    { id: "standard", label: "Standard" },
    { id: "premium", label: "Premium" },
    { id: "luxury", label: "Luxury" },
  ],
};

export default function GalleryPage() {
  const [activeFilters, setActiveFilters] = useState({
    category: "all",
    rooms: [],
    budget: [],
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects = projects.filter((project) => {
    if (
      activeFilters.category !== "all" &&
      project.category !== activeFilters.category
    ) {
      return false;
    }
    if (
      activeFilters.rooms.length > 0 &&
      !activeFilters.rooms.some((room) => project.rooms.includes(room))
    ) {
      return false;
    }
    if (
      activeFilters.budget.length > 0 &&
      !activeFilters.budget.includes(project.budget)
    ) {
      return false;
    }
    return true;
  });

  const toggleFilter = (type, value) => {
    if (type === "category") {
      setActiveFilters({ ...activeFilters, category: value });
    } else {
      const current = activeFilters[type];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      setActiveFilters({ ...activeFilters, [type]: updated });
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="py-32 lg:py-40 bg-gradient-brand">
        <div className="container-custom section-padding">
          <AnimatedSection className="max-w-3xl text-white">
            <span className="text-brand-accent text-sm font-medium uppercase tracking-wider">
              Our Portfolio
            </span>
            <h1 className="font-heading text-display-1 mt-4 mb-6 text-white">
              Real spaces. Factory-fit interiors.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Browse our portfolio of completed projects – from compact 2BHKs to
              luxury villas, homeowner spaces to B2B bulk projects.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Gallery */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <div className="flex flex-wrap gap-2">
              {filters.category.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => toggleFilter("category", cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeFilters.category === cat.id
                      ? "bg-brand-accent text-white"
                      : "bg-white text-text-secondary hover:text-brand-primary shadow-soft"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-medium text-text-secondary hover:text-brand-primary shadow-soft"
            >
              <Filter className="w-4 h-4" />
              More Filters
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <AnimatedSection className="bg-white rounded-2xl p-6 mb-10 shadow-soft">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-brand-primary mb-3">
                    By Room
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {filters.rooms.map((room) => (
                      <button
                        key={room.id}
                        onClick={() => toggleFilter("rooms", room.id)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          activeFilters.rooms.includes(room.id)
                            ? "bg-brand-accent text-white"
                            : "bg-background-card text-text-secondary hover:text-brand-primary"
                        }`}
                      >
                        {room.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-brand-primary mb-3">
                    By Budget
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {filters.budget.map((budget) => (
                      <button
                        key={budget.id}
                        onClick={() => toggleFilter("budget", budget.id)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          activeFilters.budget.includes(budget.id)
                            ? "bg-brand-accent text-white"
                            : "bg-background-card text-text-secondary hover:text-brand-primary"
                        }`}
                      >
                        {budget.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          )}

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <AnimatedSection
                key={project.id}
                delay={index * 50}
                className={
                  index === 0 || index === 5
                    ? "md:col-span-2 lg:col-span-1"
                    : ""
                }
              >
                <button
                  onClick={() => setSelectedProject(project)}
                  className="block w-full text-left group"
                >
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-4">
                    <img
                      src={project.images[0]}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-brand-primary">
                        {project.type.toUpperCase()}
                      </span>
                      <span className="px-3 py-1 bg-brand-accent/90 backdrop-blur-sm rounded-full text-xs font-medium text-white capitalize">
                        {project.budget}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-heading text-xl text-brand-primary group-hover:text-brand-accent transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-text-secondary text-sm mt-1">
                    {project.location}
                  </p>
                </button>
              </AnimatedSection>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-text-secondary">
                No projects match your filters.
              </p>
              <button
                onClick={() =>
                  setActiveFilters({ category: "all", rooms: [], budget: [] })
                }
                className="mt-4 text-brand-accent font-medium hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-brand-primary/80 backdrop-blur-sm"
            onClick={() => setSelectedProject(null)}
          />
          <div className="relative w-full max-w-4xl bg-white rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setSelectedProject(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-background-card"
            >
              <X className="w-5 h-5 text-brand-primary" />
            </button>

            <div className="aspect-video">
              <img
                src={selectedProject.images[0]}
                alt={selectedProject.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="p-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedProject.rooms.map((room) => (
                  <span
                    key={room}
                    className="px-3 py-1 bg-brand-accent/10 rounded-full text-sm text-brand-accent capitalize"
                  >
                    {room}
                  </span>
                ))}
              </div>
              <h2 className="font-heading text-h3 text-brand-primary mb-2">
                {selectedProject.title}
              </h2>
              <p className="text-text-secondary mb-4">
                {selectedProject.location}
              </p>
              <p className="text-text-primary leading-relaxed mb-6">
                {selectedProject.description}
              </p>

              {selectedProject.images.length > 1 && (
                <div className="grid grid-cols-3 gap-3">
                  {selectedProject.images.slice(1).map((img, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-xl overflow-hidden"
                    >
                      <img
                        src={img}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-border-light">
                <Link
                  to="/contact"
                  className="btn-primary"
                  onClick={() => setSelectedProject(null)}
                >
                  Start a Similar Project
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
